import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import type { QixitAuthApi } from './types';

type Props = {
  api: QixitAuthApi;
  open: boolean;
  onClose: () => void;
  initialEmail?: string;
};

export default function ForgotUsernameDialog({ api, open, onClose, initialEmail }: Props) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && initialEmail?.includes('@')) setEmail(initialEmail.trim());
  }, [open, initialEmail]);

  const handleSubmit = async () => {
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Sends every username on the email, including joint and business aliases.
      await api.post('/api/Auth/forgot-username-all', { email: email.trim() });
      setSuccess(true);
    } catch {
      setError('Failed to process request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setSuccess(false);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Forgot Username</DialogTitle>
      <DialogContent>
        {success ? (
          <Alert severity="success" sx={{ mt: 1 }}>
            If one or more accounts use this email, we sent a list of every username linked to it,
            including joint or business account aliases. Check your inbox and spam folder.
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2, mt: 1 }}>
              Enter your email address and we'll send a reminder of <strong>all usernames</strong> linked to it.
            </Typography>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <TextField
              autoFocus
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{success ? 'Close' : 'Cancel'}</Button>
        {!success && (
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Send Username List'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
